import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import { useTranslation } from 'react-i18next'
import { redirect } from 'react-router'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { localeCookie } from '#app/middleware/i18next.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import {
	isSupportedLocale,
	supportedLocales,
	toLocale,
} from '#app/utils/i18n.ts'
import { useRequestInfo } from '#app/utils/request-info.ts'
import { type Route } from './+types/locale-switch.ts'

/**
 * `locale` is narrowed through the supported-locale registry, so an unsupported
 * value fails the parse (and the action rejects it) and trimming the registry to
 * go monolingual automatically shrinks what this route accepts.
 */
const LocaleFormSchema = z.object({
	locale: z.string().refine(isSupportedLocale, 'Unsupported locale'),
	redirectTo: z.string().optional(),
})

/**
 * Persist an explicit UI-locale choice (ADR-074). Mirrors the theme/accent
 * resource routes: it always writes the dedicated locale cookie, and — for a
 * signed-in caller — also records the choice on the account as the Language
 * Preference, so it follows the user across devices. The cookie is what the
 * i18next middleware reads on the next request, so the redirect target re-renders
 * in the chosen locale.
 *
 * Unlike the theme/accent switches, the UI control is a plain document `<form>`
 * (see {@link LanguageSwitch}): it always POSTs and always redirects, so there is
 * no single-fetch `useFetcher` path here and thus no `redirectTo`-only-from-no-JS
 * dance — every submit gets a real 302 the browser follows with a full GET.
 */
export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, { schema: LocaleFormSchema })

	invariantResponse(submission.status === 'success', 'Invalid locale received')

	const { locale, redirectTo } = submission.value

	// Signed-in callers get the choice written to their account (the Language
	// Preference); an anonymous visitor keeps it on this browser via the cookie
	// alone. `getUserId` returns null for the latter, so no row is touched.
	const userId = await getUserId(request)
	if (userId) {
		await prisma.user.update({
			where: { id: userId },
			data: { locale },
			select: { id: true },
		})
	}

	return redirect(safeRedirect(redirectTo, '/'), {
		headers: { 'set-cookie': await localeCookie.serialize(locale) },
	})
}

/**
 * The AppShell language control, visible signed-in and signed-out. With only two
 * served locales it is a single toggle to the *other* locale; the visible label
 * is the target's own code (locale-neutral), and the accessible name is the
 * localized "switch to <language>". It submits as a native document `<form>` so
 * the whole page re-renders SSR in the new locale (the client i18next instance
 * reads its language from `<html lang>`, set on the server, so a full navigation
 * is what actually flips the rendered copy).
 */
export function LanguageSwitch() {
	const { i18n, t } = useTranslation()
	const requestInfo = useRequestInfo()

	const current = toLocale(i18n.language)
	const next = supportedLocales.find((locale) => locale !== current) ?? current

	return (
		<form method="POST" action="/resources/locale-switch">
			<input type="hidden" name="redirectTo" value={requestInfo.path} />
			<input type="hidden" name="locale" value={next} />
			<button
				type="submit"
				className="text-body-xs flex size-8 cursor-pointer items-center justify-center font-semibold uppercase"
			>
				{next}
				<span className="sr-only">
					{t('language.switchTo', { language: t(`language.name.${next}`) })}
				</span>
			</button>
		</form>
	)
}
